/**
 * Smart Template Engine — Preview System (Phase 1A).
 *
 * JS mirror of the backend `VideoWallController::buildTemplateBlock()` /
 * `sectionPayload()` (.claude/TEMPLATE_ENGINE_SPEC.md §2/§3). Produces the EXACT
 * same `template` payload the Android `TemplateView` consumes, so the frontend
 * `WallPreviewRenderer` is a true single-source-of-truth preview.
 *
 * Used for editor-time preview (1A/1B), the simulator (4), and the playlist
 * player (2A). Live preview (3A) instead feeds the renderer the `template` block
 * straight from RTDB (already in this shape), so it skips this assembler.
 *
 * NOTE: keep this in lockstep with buildTemplateBlock — both are bound to the spec.
 */

/** One menu section → SectionPayload (mirror of sectionPayload()). */
function sectionPayload(section, currency, resolvePhoto) {
  return {
    id: section.id,
    name_ar: section.name_ar ?? null,
    name_en: section.name_en ?? null,
    items: (section.items || [])
      .filter((it) => it.is_available === undefined || it.is_available)
      .map((it) => ({
        id: it.id,
        name_ar: it.name_ar ?? null,
        name_en: it.name_en ?? null,
        price: it.price ?? null,
        currency,
        // Live payloads already carry photo_url; editor data carries a media id
        // we resolve client-side via the wall media library.
        photo_url: it.photo_url ?? (it.photo_media_id ? resolvePhoto(it.photo_media_id) : null),
        badge_ar: it.badge_ar ?? null,
        badge_en: it.badge_en ?? null,
        description_ar: it.description_ar ?? null,
        description_en: it.description_en ?? null,
      })),
  };
}

/**
 * Assemble the `template` block from editor/menu data.
 *
 * @param {object}   args
 * @param {object}   args.instance     { type, theme_key, screen_assignments, settings }
 * @param {Array}    args.sections     menu sections [{ id, name_ar, name_en, items[] }]
 * @param {object}   args.theme        theme config (from /signage-themes) for instance.theme_key
 * @param {object}   args.defaultZones default zones (from /signage-themes)
 * @param {string}   [args.currency]   menu currency (default 'SAR')
 * @param {function} [args.resolvePhoto] (mediaId) => url
 * @returns {object} the `template` payload block (same shape as buildTemplateBlock)
 */
export function buildTemplatePreviewPayload({
  instance = {},
  sections = [],
  theme = {},
  defaultZones = {},
  currency = 'SAR',
  resolvePhoto = () => null,
}) {
  const settings = instance.settings && typeof instance.settings === 'object' ? instance.settings : {};

  const zones = settings.zones && typeof settings.zones === 'object' ? settings.zones : defaultZones;

  const rotation = {
    mode: settings.rotation_mode ?? 'none',
    interval_ms: Number(settings.rotation_interval_ms ?? 8000),
  };

  const byId = {};
  sections.forEach((s) => { byId[s.id] = s; });

  // per_screen: explicit { pos => [section_id] } when assigned, else "1" → all sections.
  const perScreen = {};
  const assignments = instance.screen_assignments && typeof instance.screen_assignments === 'object'
    ? instance.screen_assignments : {};
  if (Object.keys(assignments).length) {
    Object.entries(assignments).forEach(([pos, ids]) => {
      const picked = (Array.isArray(ids) ? ids : [ids])
        .map((sid) => byId[sid])
        .filter(Boolean);
      perScreen[String(pos)] = { sections: picked.map((s) => sectionPayload(s, currency, resolvePhoto)) };
    });
  } else {
    perScreen['1'] = { sections: sections.map((s) => sectionPayload(s, currency, resolvePhoto)) };
  }

  return {
    type: instance.type ?? 'menu',
    theme: {
      key: theme.key ?? 'noir_gold',
      background_kind: theme.background_kind ?? 'gradient',
      background_url: theme.background_url ?? null,
      gradient: theme.gradient ?? ['#1A1A1A', '#000000'],
      text_color: theme.text_color ?? '#FFFFFF',
      accent_color: theme.accent_color ?? '#D4AF37',
      text_shadow: theme.text_shadow ?? 'dark',
      font_key: theme.font_key ?? 'tajawal',
      logo_url: settings.logo_url ?? null,
    },
    zones,
    rotation,
    per_screen: perScreen,
  };
}
